/**
 * Search Results — categorized layout enhancement (currently dormant)
 *
 * NOTE: the categorized renderer (`categorizeResultsByField`) was dropped from
 * scripts.html in favor of the BlazeCU-style Category facet sidebar, so no
 * category sections exist and every result falls back to the plain is-page
 * style. Kept in case the per-type layouts return.
 *
 * Cludo's `categorizeResultsByField` config splits results
 * into stacked sections (Products / Pages / FAQs) with per-section counts and a
 * native "Show all" button. This module layers the *visual* differences on top:
 * it tags each rendered result with a content-type modifier class so the three
 * sections can be styled differently (card / list / accordion), and wires up the
 * FAQ accordion toggle.
 *
 * Styling is driven by OUR OWN modifier classes (is-product / is-faq / is-page),
 * not Cludo's internal markup — so the SCSS doesn't depend on the minified
 * bundle's class names. The only runtime coupling is finding the result items
 * and reading their content-type (the two TODOs below), both of which depend on
 * what the live index returns.
 *
 * Cludo renders asynchronously and re-renders on pagination / "Show all", so we
 * re-run on every container mutation plus the public `cludoSearchSuccess` event.
 */
export function searchResults() {

    const container = document.querySelector('#cludo-search-results');
    if (!container) {
        return; // not on the search page
    }

    // Single result item — this IS a real Cludo class (already used in our SCSS).
    const ITEM_SELECTOR = '.cludo-search-results-item';

    // Map the index ContentType value -> our modifier class. Anything not listed
    // falls back to the plain page-list style (is-page).
    const TYPE_CLASS = {
        Products: 'is-product',
        Product: 'is-product',
        FAQs: 'is-faq',
        FAQ: 'is-faq',
    };

    // TODO: confirm against the live categorized DOM. We need the content-type
    // value for each result so we can pick its layout. With categorizeResultsByField
    // the value typically lives on the category section wrapper or its heading;
    // adjust this to read the real attribute/element once you can inspect a real
    // response. Returns '' when unknown (-> falls back to is-page).
    function getContentType(item) {
        const section = item.closest('[data-cludo-category]');
        if (section) {
            return section.getAttribute('data-cludo-category') || '';
        }
        return '';
    }

    function enhance() {
        container.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
            if (item.dataset.enhanced) {
                return; // already processed
            }
            item.dataset.enhanced = 'true';

            const modifier = TYPE_CLASS[getContentType(item)] || 'is-page';
            item.classList.add('search-result', modifier);

            if (modifier === 'is-faq') {
                buildFaqAccordion(item);
            }
        });
    }

    // Make an FAQ result collapsible: click the title to toggle its answer.
    function buildFaqAccordion(item) {
        const title = item.querySelector('.cludo-search-results-item__title');
        if (!title) {
            return;
        }
        title.setAttribute('role', 'button');
        title.setAttribute('tabindex', '0');
        title.setAttribute('aria-expanded', 'false');

        const toggle = () => {
            const open = item.classList.toggle('is-open');
            title.setAttribute('aria-expanded', String(open));
        };
        title.addEventListener('click', toggle);
        title.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle();
            }
        });
    }

    // Re-run whenever Cludo re-renders. We only add classes/attributes/listeners
    // (no child nodes), so enhance() won't retrigger the childList observer — but
    // we still disconnect/reconnect defensively in case that changes.
    const observer = new MutationObserver(() => {
        observer.disconnect();
        enhance();
        observer.observe(container, { childList: true, subtree: true });
    });
    observer.observe(container, { childList: true, subtree: true });

    // First paint / new searches: enhance once Cludo has rendered the markup.
    window.addEventListener('cludoSearchSuccess', () => {
        window.requestAnimationFrame(enhance);
    });

    enhance(); // in case results are already present
}
